import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, FileText, ArrowRight, Loader2, Flame, Zap, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const options = [
  { count: 25, time: "30 min", label: "Quick Round", description: "Fast practice session" },
  { count: 50, time: "60 min", label: "Half Paper", description: "Balanced preparation" },
  { count: 100, time: "120 min", label: "Full Paper", description: "Complete UPSC simulation" },
];

const difficultyOptions = [
  { id: "all", label: "Mixed", description: "All difficulty levels", icon: BarChart3, color: "text-primary" },
  { id: "medium_hard", label: "Medium + Hard", description: "No easy questions", icon: Zap, color: "text-amber-400" },
  { id: "hard", label: "Hard Only", description: "UPSC-level elimination", icon: Flame, color: "text-red-400" },
];

const topicWeights = [
  { name: "Environment", weight: 20, color: "#10b981" },
  { name: "Polity", weight: 18, color: "#6366f1" },
  { name: "Economy", weight: 14, color: "#f59e0b" },
  { name: "History", weight: 12, color: "#ec4899" },
  { name: "Geography", weight: 12, color: "#8b5cf6" },
  { name: "Science", weight: 10, color: "#3b82f6" },
  { name: "Current Affairs", weight: 8, color: "#14b8a6" },
  { name: "Art & Culture", weight: 6, color: "#f97316" },
];

export default function TestSetup() {
  const [selected, setSelected] = useState(null);
  const [difficulty, setDifficulty] = useState("hard");
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/tests/generate`, { question_count: selected, difficulty });
      toast.success(`Test ${res.data.test_code} generated!`);
      navigate(`/test/${res.data.test_id}`);
    } catch (err) {
      toast.error("Failed to generate test. Please try again.");
      setGenerating(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="test-setup-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Generate Test
        </h1>
        <p className="text-muted-foreground mt-2 text-base">
          Select paper length and start your mock examination
        </p>
      </div>

      {/* Paper Length Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {options.map((opt) => (
          <Card
            key={opt.count}
            data-testid={`test-option-${opt.count}`}
            className={`cursor-pointer transition-all duration-300 hover:-translate-y-1 ${
              selected === opt.count
                ? "border-primary bg-primary/5 shadow-lg shadow-indigo-500/10"
                : "bg-card border-border hover:border-primary/30"
            }`}
            onClick={() => setSelected(opt.count)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Badge
                  variant={selected === opt.count ? "default" : "outline"}
                  className={selected === opt.count ? "bg-primary" : ""}
                >
                  {opt.label}
                </Badge>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Clock size={14} />
                  <span className="text-sm">{opt.time}</span>
                </div>
              </div>
              <div className="font-mono text-5xl font-bold mb-2">
                {opt.count}
              </div>
              <p className="text-sm text-muted-foreground">{opt.description}</p>
              <div className="mt-4 flex items-center gap-1.5">
                <FileText size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Questions | +2 / -0.67 marks | Max {opt.count * 2} marks
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Difficulty Selection */}
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Difficulty Level</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {difficultyOptions.map((opt) => {
            const Icon = opt.icon;
            return (
              <Card
                key={opt.id}
                data-testid={`difficulty-${opt.id}`}
                className={`cursor-pointer transition-all duration-300 hover:-translate-y-0.5 ${
                  difficulty === opt.id
                    ? "border-primary bg-primary/5 shadow-lg shadow-indigo-500/10"
                    : "bg-card border-border hover:border-primary/30"
                }`}
                onClick={() => setDifficulty(opt.id)}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <Icon size={20} className={difficulty === opt.id ? opt.color : "text-muted-foreground"} />
                  <div>
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Topic Distribution Preview */}
      <Card className="bg-card border-border" data-testid="topic-distribution">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Expected Topic Distribution</CardTitle>
          <p className="text-xs text-muted-foreground">Based on last 10 years UPSC Prelims pattern analysis</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topicWeights.map((t) => {
              const qCount = selected ? Math.round(selected * t.weight / 100) : 0;
              return (
                <div key={t.name} className="flex items-center gap-3">
                  <span className="text-sm w-32 text-muted-foreground">{t.name}</span>
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${t.weight}%`, backgroundColor: t.color }}
                    />
                  </div>
                  <span className="font-mono text-xs w-16 text-right">{t.weight}%</span>
                  {selected && (
                    <span className="font-mono text-xs w-10 text-right text-muted-foreground">~{qCount}Q</span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div className="flex justify-end">
        <Button
          data-testid="generate-test-btn"
          size="lg"
          disabled={!selected || generating}
          onClick={handleGenerate}
          className="bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/20 gap-2 px-8"
        >
          {generating ? (
            <><Loader2 size={18} className="animate-spin" /> Generating...</>
          ) : (
            <>Start Examination <ArrowRight size={18} /></>
          )}
        </Button>
      </div>
    </div>
  );
}
